import { useEffect, useMemo, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

const HIDE_DELAY_MS = 220;

export function ActivityBar({ className }: { className?: string }) {
  const fetchingCount = useIsFetching();
  const mutatingCount = useIsMutating();
  const isActive = fetchingCount + mutatingCount > 0;

  const [isVisible, setIsVisible] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'active' | 'settling'>('idle');
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;

    const update = () => setReduceMotion(Boolean(query.matches));
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      setPhase('active');
      return;
    }

    if (!isVisible) {
      setPhase('idle');
      return;
    }

    setPhase('settling');
    const timer = window.setTimeout(() => {
      setIsVisible(false);
      setPhase('idle');
    }, HIDE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isActive, isVisible]);

  const label = useMemo(() => {
    if (!isActive) return 'Idle';
    if (mutatingCount > 0) return 'Saving…';
    return 'Loading…';
  }, [isActive, mutatingCount]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className={cn('fixed inset-x-0 top-0 z-[60] h-1', className)}
      title={label}
    >
      <div
        className={cn(
          'h-full origin-left rounded-b-full bg-gradient-to-r from-brand-600 via-gold-500 to-brand-700',
          'transition-opacity duration-200',
          phase === 'settling' ? 'opacity-0' : 'opacity-100'
        )}
        style={{
          animation:
            phase === 'active' && !reduceMotion
              ? 'karebe-activity-bar 1.1s ease-in-out infinite'
              : undefined,
        }}
      />
    </div>
  );
}

export default ActivityBar;
